import { useCallback, useEffect, useRef, useState } from "react";

import { useFeatureToggle } from "@bokio/hooks/useFeatureToggle/useFeatureToggle";
import { languageNotifier } from "@bokio/lang/languageNotifier";
import * as m from "@bokio/mobile-web-shared/core/model/model";
import { useActiveBankConnection } from "@bokio/mobile-web-shared/hooks/useActiveBankConnection/useActiveBankConnection";
import { useLazyApi } from "@bokio/mobile-web-shared/hooks/useApi/useApi";
import * as proxy from "@bokio/mobile-web-shared/services/api/proxy";
import { useCompanyUser } from "@bokio/shared/state/requests";

import { PaymentContext } from "./PaymentContext";

import type { PaymentContextValue } from "./PaymentContext";
import type { FC, PropsWithChildren } from "react";

type UserPaymentPermissionDto = m.Bokio.Bank.Contract.Dtos.UserPaymentPermissionDto;
export const PaymentContextProvider: FC<PropsWithChildren> = ({ children }) => {
	const { company, companyUserPermissions } = useCompanyUser();
	// BBA multiple accounts refactoring:
	// Currently only looks at the external system in the backend,
	// but we probably want to keep checking bank account ID to allow specific rules in savings account,
	// e.g.:
	// - to allow international payments or not (should probably in the bank (bank account) config),
	// - to allow BG/PG payment or not,
	// - to allow to order card or not
	// - to decide should you be able to make supplier/salary/expense/tax... payments.
	const { activeBusinessAccount } = useActiveBankConnection();
	const [currentUserPermission, setCurrentUserPermission] = useState<UserPaymentPermissionDto>({
		CanSignPayment: false,
	});

	// Using ref lock because request.isLoading is updated by setState,
	// which means it will stay false before state updates are applied.
	// Therefore if we only do `const refresh = () => !isLoading && execute();`,
	// the execute might be called multiple times if there are multiple calls to the refresh.
	// (i.e. if there are multiple components getting the same request from context, for example `CreatePaymentScene`.)
	const countryListRequestLock = useRef(false);
	const currencyListRequestLock = useRef(false);

	const [executeCountryListRequest, countryListRequest] = useLazyApi(
		proxy.Bank.LocaleInfoController.GetCountryList.Get,
	);
	const [executeCurrencyList, currencyListRequest] = useLazyApi(proxy.Bank.LocaleInfoController.GetCurrencyList.Get);
	const [getPermissionOfCurrentUser, getPermissionOfCurrentUserRequest] = useLazyApi(
		proxy.Bank.PaymentController.GetPermissionOfCurrentUser.Get,
	);

	const { isFeatureEnabled } = useFeatureToggle();

	const [signablePaymentCount, setSignablePaymentCount] = useState(0);
	const [executeGetSignablePaymentCount] = useLazyApi(proxy.Bank.PaymentController.GetSignablePaymentCount.Get, {
		onSuccess: setSignablePaymentCount,
	});

	const companyId = company?.Id;
	const userHasUploadBankPermission = !!companyUserPermissions?.UploadBank;
	const bankAccountId = activeBusinessAccount?.BankAccountId;

	const refreshSignablePaymentCount = useCallback(() => {
		const isSveaDelegatesEnabled = isFeatureEnabled(m.Core.Features.SveaDelegates);
		const canCallCount = isSveaDelegatesEnabled
			? userHasUploadBankPermission
			: currentUserPermission.CanSignPayment && userHasUploadBankPermission;
		if (canCallCount && companyId) {
			return executeGetSignablePaymentCount(companyId);
		}
		return undefined;
	}, [
		companyId,
		currentUserPermission.CanSignPayment,
		executeGetSignablePaymentCount,
		isFeatureEnabled,
		userHasUploadBankPermission,
	]);

	useEffect(() => {
		refreshSignablePaymentCount();
	}, [refreshSignablePaymentCount]);

	const refreshPaymentPermission = useCallback(async () => {
		if (userHasUploadBankPermission && companyId) {
			const res = await getPermissionOfCurrentUser(companyId);
			if (res.Data) {
				setCurrentUserPermission(res.Data);
			}
		}
	}, [companyId, userHasUploadBankPermission, getPermissionOfCurrentUser]);

	const [getPaymentValidationRules, getPaymentValidationRequest] = useLazyApi(
		proxy.Bank.PaymentController.GetPaymentValidationRules.Get,
	);

	// TODO Refresh paymentValidationRules at midnight because next
	// possible banking day will change.
	const refreshPaymentValidationRules = useCallback(async () => {
		if (userHasUploadBankPermission && companyId && bankAccountId) {
			getPaymentValidationRules(companyId, bankAccountId);
		}
	}, [userHasUploadBankPermission, companyId, getPaymentValidationRules, bankAccountId]);

	const refreshCountryListIfNeeded = async (force?: boolean) => {
		if ((force || !countryListRequest.data?.Data) && !countryListRequestLock.current && companyId) {
			countryListRequestLock.current = true;
			try {
				await executeCountryListRequest(companyId, {
					Consumer: m.Bokio.Bank.Contract.Utils.CountryListConsumer.Payment,
				});
			} finally {
				countryListRequestLock.current = false;
			}
		}
	};

	const refreshCurrencyListIfNeeded = async (force?: boolean) => {
		if ((force || !currencyListRequest.data?.Data) && !currencyListRequestLock.current && companyId) {
			currencyListRequestLock.current = true;
			try {
				await executeCurrencyList(companyId);
			} finally {
				currencyListRequestLock.current = false;
			}
		}
	};

	useEffect(() => {
		refreshPaymentPermission();
	}, [refreshPaymentPermission]);

	useEffect(() => {
		refreshPaymentValidationRules();
	}, [refreshPaymentValidationRules]);

	languageNotifier.subscribe(() => {
		refreshCountryListIfNeeded(true);
		refreshCurrencyListIfNeeded(true);
	});

	const value: PaymentContextValue = {
		get currentUserPermission() {
			return currentUserPermission;
		},
		get refreshPaymentPermission() {
			return refreshPaymentPermission;
		},
		get paymentValidationRulesRequest() {
			return getPaymentValidationRequest;
		},
		get paymentValidationRules() {
			return getPaymentValidationRequest.data?.Data;
		},
		get getPermissionOfCurrentUserRequest() {
			return getPermissionOfCurrentUserRequest;
		},
		// Defer getting these because they are only used in very few places
		get countryListRequest() {
			setImmediate(() => {
				refreshCountryListIfNeeded();
			});
			return countryListRequest;
		},
		get currencyListRequest() {
			setImmediate(() => {
				refreshCurrencyListIfNeeded();
			});
			return currencyListRequest;
		},
		get signablePaymentCount() {
			return signablePaymentCount;
		},
		get refreshSignablePaymentCount() {
			return refreshSignablePaymentCount;
		},
	};

	return <PaymentContext value={value}>{children}</PaymentContext>;
};
